import React, { useEffect, useRef, useState } from "react";
import api, { formatError, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, StatCard, LoadingState, TableLoadingSkeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, Paperclip, DownloadSimple, TrendUp, TrendDown, FileCsv, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const CATS = [
    "Maintenance & Repairs", "Electricity", "Water", "Security", "Housekeeping",
    "Staff", "Plumbing", "Electrical", "Lift / Elevator", "Gardening",
    "Pest Control", "AMC / Contracts", "Cleaning Materials", "Office / Administration",
    "Events / Community", "Legal / Professional", "Other",
];
const MODES = { cash: "Cash", upi: "UPI", bank_transfer: "Bank Transfer", cheque: "Cheque", card: "Card", other: "Other" };
const emptyForm = () => ({ title: "", amount: "", category: "Maintenance & Repairs", date: new Date().toISOString().slice(0, 10), description: "", receipt_file_id: "", vendor: "", payment_mode: "cash", reference_number: "", notes: "" });

export default function Expenses() {
    const { user } = useAuth();
    const [expenses, setExpenses] = useState([]);
    const [stats, setStats] = useState(null);
    const [open, setOpen] = useState(false);
    const [csvOpen, setCsvOpen] = useState(false);
    const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
    const [form, setForm] = useState(emptyForm());
    const [editingId, setEditingId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [receiptName, setReceiptName] = useState("");
    const [loading, setLoading] = useState(true);
    const fileRef = useRef();
    const csvFileRef = useRef();

    const isStaff = user?.role === "admin" || user?.role === "committee";
    const isResident = user?.role === "resident";

    async function load() {
        setLoading(true);
        try {
            const calls = [api.get("/expenses")];
            // Both staff and residents can see the monthly dashboard
            if (isStaff || isResident) calls.push(api.get(`/expenses/stats?month=${reportMonth}`));
            const results = await Promise.all(calls);
            setExpenses(results[0].data);
            if (isStaff || isResident) setStats(results[1].data);
        }
        catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [reportMonth]);

    async function uploadReceipt(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm(f => ({ ...f, receipt_file_id: data.id }));
            setReceiptName(file.name);
            toast.success("Receipt uploaded");
        } catch (e) { toast.error(formatError(e)); }
        finally { setUploading(false); }
    }

    async function submit(e) {
        e.preventDefault();
        try {
            const payload = { ...form, amount: parseFloat(form.amount) };
            if (editingId) {
                await api.patch(`/expenses/${editingId}`, payload);
                toast.success("Expense updated");
            } else {
                await api.post("/expenses", payload);
                toast.success("Expense recorded");
            }
            setOpen(false);
            setEditingId(null);
            setForm(emptyForm());
            setReceiptName("");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }
    function openCreate() {
        setEditingId(null);
        setForm(emptyForm());
        setReceiptName("");
        setOpen(true);
    }
    function openEdit(exp) {
        setEditingId(exp.id);
        setForm({
            title: exp.title || "",
            amount: String(exp.amount || ""),
            category: exp.category || "Maintenance & Repairs",
            date: (exp.date || "").slice(0, 10),
            description: exp.description || "",
            receipt_file_id: exp.receipt_file_id || "",
            vendor: exp.vendor || "",
            payment_mode: exp.payment_mode || "cash",
            reference_number: exp.reference_number || "",
            notes: exp.notes || "",
        });
        setReceiptName(exp.receipt_file_id ? "attached" : "");
        setOpen(true);
    }
    async function remove(exp) {
        const reason = window.prompt(`Void this expense? This preserves the record for audit.\n\nOptional reason:`);
        if (reason === null) return;
        try {
            await api.delete(`/expenses/${exp.id}${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`);
            toast.success("Expense voided");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function importCSV(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        
        const fd = new FormData();
        fd.append("file", file);
        
        try {
            const { data } = await api.post("/expenses/import-csv", fd, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            
            if (data.errors && data.errors.length > 0) {
                toast.warning(`Imported ${data.imported} expenses. ${data.errors.length} errors.`);
                console.log("Import errors:", data.errors);
            } else {
                toast.success(`Successfully imported ${data.imported} expenses`);
            }
            
            setCsvOpen(false);
            load();
        } catch (e) {
            toast.error(formatError(e));
        }
        
        // Reset file input
        if (csvFileRef.current) csvFileRef.current.value = "";
    }

    async function downloadMonthlyReport() {
        try {
            const response = await api.get(`/expenses/report/csv?month=${reportMonth}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `financial-report-${reportMonth}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Monthly report downloaded!");
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    function downloadUrl(fid) {
        const token = localStorage.getItem("maintayn_token");
        return `${API_BASE}/files/${fid}/download?auth=${encodeURIComponent(token || "")}`;
    }

    const total = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Books"
                title="Expenses"
                description="Log every rupee spent on society upkeep — with receipts attached."
                actions={isStaff && (
                    <div className="flex gap-2">
                        <Button onClick={downloadMonthlyReport} variant="outline" className="rounded-full border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white">
                            <FileCsv size={16} className="mr-1.5" /> Monthly Report
                        </Button>
                        <Button onClick={() => setCsvOpen(true)} variant="outline" className="rounded-full border-brand-action text-brand-action hover:bg-brand-action hover:text-white">
                            <UploadSimple size={16} className="mr-1.5" /> Bulk Import
                        </Button>
                        <Button data-testid="add-expense-btn" onClick={openCreate} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white active:scale-[0.98]">
                            <Plus size={16} className="mr-1.5" /> Log expense
                        </Button>
                    </div>
                )}
            />

            {(isStaff || isResident) && stats && (
                <div data-testid="expense-dashboard" className="mb-8 space-y-4">
                    {/* Month Selector */}
                    <div className="flex items-center gap-4 bg-white border border-brand-line rounded-sm p-4">
                        <Label className="text-sm font-medium text-brand-ink">View Month:</Label>
                        <Input
                            type="month"
                            value={reportMonth}
                            onChange={(e) => setReportMonth(e.target.value)}
                            className="w-48 rounded-sm border-brand-line"
                        />
                        <p className="text-xs text-brand-inkSoft">Dashboard and reports will show data for {reportMonth}</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <StatCard label="Income" value={inr(stats.totals.income)} hint={`Paid invoices in ${reportMonth}`} />
                        <StatCard label="Spent" value={inr(stats.totals.spent)} hint={`Across ${stats.categories.length} categories`} />
                        <StatCard label="Net" value={inr(stats.totals.net)} hint={stats.totals.net >= 0 ? "Surplus" : "Deficit"} accent={stats.totals.net < 0} />
                        <StatCard label="Balance Collection" value={inr(stats.totals.balance_collection || 0)} hint={`${stats.counts?.pending_invoices || 0} unpaid invoices`} accent={stats.totals.balance_collection > 0} />
                    </div>

                    {stats.series && stats.series.length > 1 && stats.projection && (
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="md:col-span-2 bg-white border border-brand-line rounded-sm p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <TrendUp size={16} className="text-brand-action" weight="duotone" />
                                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Income vs Spent · last 12 months + next 3 (projected)</p>
                            </div>
                            <div className="flex items-end gap-2 h-40">
                                {[...stats.series, ...stats.projection].map((t, i) => {
                                    const combined = [...stats.series, ...stats.projection];
                                    const max = Math.max(...combined.flatMap(x => [x.income, x.spent]), 1);
                                    const isProj = i >= stats.series.length;
                                    const iH = (t.income / max) * 100;
                                    const sH = (t.spent / max) * 100;
                                    return (
                                        <div key={`${t.month}-${i}`} className="flex-1 flex flex-col items-center gap-1" data-testid={`expense-bar-${t.month}`}>
                                            <div className="w-full flex items-end gap-0.5 flex-1 relative">
                                                <div className={`flex-1 rounded-t-sm ${isProj ? "bg-[#059669]/40 border border-dashed border-[#059669]/60" : "bg-[#059669]"}`} style={{ height: `${iH}%` }} title={`Income ${inr(t.income)}`}></div>
                                                <div className={`flex-1 rounded-t-sm ${isProj ? "bg-brand-action/40 border border-dashed border-brand-action/60" : "bg-brand-action"}`} style={{ height: `${sH}%` }} title={`Spent ${inr(t.spent)}`}></div>
                                            </div>
                                            <p className={`text-[9px] ${isProj ? "text-brand-inkSoft italic" : "text-brand-inkSoft"}`}>{t.month.slice(5)}</p>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex flex-wrap gap-4 mt-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">
                                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-[#059669] rounded-sm"></span> Income</span>
                                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-brand-action rounded-sm"></span> Spent</span>
                                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-brand-action/40 border border-dashed border-brand-action/60 rounded-sm"></span> Projected</span>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Category breakdown - always show */}
                    <div className="bg-white border border-brand-line rounded-sm p-5" data-testid="expense-categories">
                        <div className="flex items-center gap-2 mb-4">
                            <TrendDown size={16} className="text-brand-action" weight="duotone" />
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Spent by category · {reportMonth}</p>
                        </div>
                        {stats.categories.length === 0 ? (
                            <p className="text-xs text-brand-inkSoft">No expenses this month.</p>
                        ) : (
                            <div className="space-y-3">
                                {stats.categories.slice(0, 6).map(c => {
                                    const pct = stats.totals.spent > 0 ? (c.total / stats.totals.spent) * 100 : 0;
                                    return (
                                        <div key={c.category}>
                                            <div className="flex items-center justify-between text-xs text-brand-ink">
                                                <span className="capitalize font-medium">{c.category}</span>
                                                <span className="text-brand-inkSoft">{inr(c.total)}</span>
                                            </div>
                                            <div className="h-1.5 bg-brand-sage/50 rounded-full mt-1 overflow-hidden">
                                                <div className="h-full bg-brand-action" style={{ width: `${pct}%` }}></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="bg-white border border-brand-line rounded-sm p-6 mb-6 flex items-center justify-between">
                <div>
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Total logged</p>
                    <p className="font-heading text-3xl text-brand-ink tracking-tight mt-1">{inr(total)}</p>
                </div>
                <div className="text-right">
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Entries</p>
                    <p className="font-heading text-3xl text-brand-ink tracking-tight mt-1">{expenses.length}</p>
                </div>
            </div>

            {loading ? (
                <LoadingState message="Loading expenses..." />
            ) : expenses.length === 0 ? (
                <EmptyState title="No expenses yet" description="Log your first society expense to start tracking."
                    image="https://images.unsplash.com/photo-1704310546522-59f10c7ec294?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwZW1wdHklMjByb29tJTIwc3VubGlnaHR8ZW58MHx8fHwxNzg0NjIzMzM0fDA&ixlib=rb-4.1.0&q=85" />
            ) : (
                <>
                    {/* Mobile scroll hint */}
                    <div className="md:hidden bg-brand-sage/30 border border-brand-action/20 rounded-sm px-3 py-2 mb-3 flex items-center gap-2 text-xs text-brand-ink">
                        <span className="flex-shrink-0">💡</span>
                        <span>Scroll table horizontally to see all columns and actions →</span>
                    </div>
                    <div className="bg-white border border-brand-line rounded-sm overflow-x-auto">
                    <table className="w-full text-sm min-w-[700px]">
                        <thead>
                            <tr className="bg-brand-bg border-b border-brand-line text-left">
                                {["Date", "Title", "Category", "Vendor", "Mode", "Reference", "Amount", "By", "Receipt", ""].map(h => (
                                    <th key={h} className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {expenses.map((e) => (
                                <tr key={e.id} className="border-b border-brand-line/60 hover:bg-brand-bg/50" data-testid={`expense-row-${e.id}`}>
                                    <td className="py-3 px-4 text-brand-inkSoft whitespace-nowrap">{e.date}</td>
                                    <td className="py-3 px-4 font-medium text-brand-ink">
                                        {e.title}
                                        {e.description && <div className="text-xs text-brand-inkSoft mt-1">{e.description}</div>}
                                    </td>
                                    <td className="py-3 px-4 whitespace-nowrap"><Chip>{e.category}</Chip></td>
                                    <td className="py-3 px-4 text-brand-inkSoft whitespace-nowrap">{e.vendor || "—"}</td>
                                    <td className="py-3 px-4 text-brand-inkSoft whitespace-nowrap">{MODES[e.payment_mode] || "—"}</td>
                                    <td className="py-3 px-4 text-brand-inkSoft text-xs whitespace-nowrap">{e.reference_number || "—"}</td>
                                    <td className="py-3 px-4 font-heading text-brand-ink whitespace-nowrap">{inr(e.amount)}</td>
                                    <td className="py-3 px-4 text-brand-inkSoft whitespace-nowrap">{e.created_by_name}</td>
                                    <td className="py-3 px-4 whitespace-nowrap">
                                        {e.receipt_file_id ? (
                                            <a href={downloadUrl(e.receipt_file_id)} target="_blank" rel="noreferrer" className="text-brand-action hover:underline text-xs inline-flex items-center gap-1">
                                                <DownloadSimple size={14} /> View
                                            </a>
                                        ) : <span className="text-brand-inkSoft text-xs">—</span>}
                                    </td>
                                    <td className="py-3 px-4 text-right whitespace-nowrap">
                                        {isStaff && (
                                            <>
                                                <button data-testid={`edit-expense-${e.id}`} onClick={() => openEdit(e)} className="p-1.5 hover:bg-brand-sage/40 rounded-sm text-brand-inkSoft mr-1" title="Edit">✎</button>
                                                <button data-testid={`delete-expense-${e.id}`} onClick={() => remove(e)} className="p-1.5 hover:bg-[#FEF2F2] rounded-sm text-brand-action" title="Void"><Trash size={14} /></button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm">
                    <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">{editingId ? "Edit expense" : "Log expense"}</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="space-y-2"><Label>Title</Label>
                            <Input data-testid="expense-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-2"><Label>Amount (₹)</Label>
                                <Input data-testid="expense-amount-input" required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2"><Label>Category</Label>
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                    <SelectTrigger data-testid="expense-category-trigger" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2"><Label>Date</Label>
                                <Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>Vendor / Payee</Label>
                                <Input data-testid="expense-vendor-input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="e.g. Otis Elevators" className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2"><Label>Payment mode</Label>
                                <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                                    <SelectTrigger data-testid="expense-mode-trigger" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>{Object.entries(MODES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2"><Label>Reference / transaction no.</Label>
                            <Input data-testid="expense-reference-input" value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} placeholder="UTR / cheque no. / receipt no." className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-2"><Label>Description</Label>
                            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-2"><Label>Notes (internal)</Label>
                            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-2">
                            <Label>Receipt (optional)</Label>
                            <input ref={fileRef} type="file" onChange={uploadReceipt} accept="image/*,application/pdf" className="hidden" />
                            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="expense-upload-btn"
                                className="rounded-full border-brand-line w-full">
                                <Paperclip size={14} className="mr-1.5" />
                                {uploading ? "Uploading..." : receiptName || "Attach receipt"}
                            </Button>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="expense-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">Save</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* CSV Import Dialog */}
            <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
                <DialogContent className="sm:max-w-md bg-brand-bg border-brand-line">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-brand-ink">Bulk Import Expenses</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-brand-inkSoft">
                            Upload a CSV file with columns: <strong>description, amount, category, date, payment_method</strong> (optional: notes)
                        </p>
                        <div className="bg-white border border-brand-line rounded-sm p-3">
                            <p className="text-xs text-brand-inkSoft mb-2">Example CSV format:</p>
                            <pre className="text-[10px] bg-brand-bg p-2 rounded overflow-x-auto">
description,amount,category,date,payment_method
Generator Maintenance,15000,maintenance,2026-07-15,bank_transfer
Security Cameras Repair,8500,security,2026-07-18,cash
                            </pre>
                        </div>
                        <div className="space-y-2">
                            <Label>Select CSV File</Label>
                            <input
                                ref={csvFileRef}
                                type="file"
                                accept=".csv"
                                onChange={importCSV}
                                className="block w-full text-sm text-brand-ink file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-brand-action file:text-white hover:file:bg-brand-actionHover"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setCsvOpen(false)} className="rounded-full">Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Monthly Report Download - Month Selector */}
            <Dialog open={false}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Download Monthly Report</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <Label>Select Month</Label>
                        <Input
                            type="month"
                            value={reportMonth}
                            onChange={(e) => setReportMonth(e.target.value)}
                            className="rounded-sm"
                        />
                    </div>
                    <DialogFooter>
                        <Button onClick={downloadMonthlyReport} className="rounded-full">
                            <DownloadSimple size={16} className="mr-1.5" /> Download
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
